import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { User } from 'firebase/auth';
import { authRepository as defaultAuthRepository, type AuthRepository } from '@/infrastructure/firebase/authRepository';
import { profileRepository as defaultProfileRepository, type ProfileRepository } from '@/infrastructure/firebase/profileRepository';

interface AuthContextType {
  user: User | null;
  userRole: string | null;
  loading: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error: { message: string } | null }>;
  signUp: (email: string, password: string, name: string, role?: string) => Promise<{ error: { message: string } | null }>;
  signOut: () => Promise<void>;
}

export interface AuthProviderProps {
  children: ReactNode;
  authRepository?: AuthRepository;
  profileRepository?: ProfileRepository;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  userRole: null,
  loading: true,
  isAdmin: false,
  signIn: async () => ({ error: null }),
  signUp: async () => ({ error: null }),
  signOut: async () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({
  children,
  authRepository = defaultAuthRepository,
  profileRepository = defaultProfileRepository,
}: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    return authRepository.onAuthChange(async (nextUser) => {
      setUser(nextUser);
      if (!nextUser) {
        setUserRole(null);
        setLoading(false);
        return;
      }

      const profile = await profileRepository.getProfile(nextUser.uid);
      setUserRole(profile?.role || 'user');
      setLoading(false);
    });
  }, [authRepository, profileRepository]);

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      await authRepository.signIn(email, password);
      return { error: null };
    } catch (error) {
      return { error: { message: error instanceof Error ? error.message : 'Sign in failed' } };
    }
  }, [authRepository]);

  const signUp = useCallback(async (email: string, password: string, name: string, role = 'user') => {
    try {
      await authRepository.signUp(email, password, name, role);
      return { error: null };
    } catch (error) {
      return { error: { message: error instanceof Error ? error.message : 'Sign up failed' } };
    }
  }, [authRepository]);

  const signOut = useCallback(async () => {
    await authRepository.signOut();
  }, [authRepository]);

  const isAdmin = userRole === 'admin';

  return (
    <AuthContext.Provider value={{ user, userRole, loading, isAdmin, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}
